import { useState, useEffect, useCallback } from 'react';
import { 
  Template, 
  TemplateListState, 
  TemplateEditorState,
  TemplateFilters,
  TemplateCreateRequest,
  TemplateUpdateRequest,
  TemplateListResponse,
  TemplateResponse
} from '../types';

// Custom hook for managing templates state and operations
export const useTemplates = () => {
  // List state
  const [listState, setListState] = useState<TemplateListState>({
    templates: [],
    loading: false,
    error: null,
    filters: {
      search: '',
      category: 'all',
      difficulty: 'all',
      tags: []
    },
    pagination: {
      limit: 20,
      offset: 0,
      total: 0,
      hasMore: false
    }
  });

  // Editor state
  const [editorState, setEditorState] = useState<TemplateEditorState>({
    selectedTemplate: null,
    isEditing: false,
    isDirty: false,
    isGenerating: false,
    generationError: null
  });

  // Fetch templates from API
  const fetchTemplates = useCallback(async (isLoadMore = false, customFilters?: Partial<TemplateFilters>) => {
    try {
      if (!isLoadMore) {
        setListState(prev => ({ ...prev, loading: true, error: null }));
      }

      const filters = customFilters || listState.filters;
      const offset = isLoadMore ? listState.pagination.offset + listState.pagination.limit : 0;
      
      const params = new URLSearchParams({
        limit: listState.pagination.limit.toString(),
        offset: offset.toString(),
      });

      if (filters.search.trim()) {
        params.append('search', filters.search.trim());
      }
      if (filters.category !== 'all') {
        params.append('category', filters.category);
      }
      if (filters.difficulty !== 'all') {
        params.append('difficulty', filters.difficulty);
      }
      if (filters.tags.length > 0) {
        params.append('tags', filters.tags.join(','));
      }

      const response = await fetch(`/api/neural-templates/list?${params}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch templates (Status: ${response.status})`);
      }

      const data: TemplateListResponse = await response.json();

      setListState(prev => ({
        ...prev,
        templates: isLoadMore ? [...prev.templates, ...data.templates] : data.templates,
        loading: false,
        pagination: {
          ...prev.pagination,
          total: data.pagination.total,
          offset: data.pagination.offset,
          hasMore: data.pagination.hasMore
        },
        filters: filters
      }));

    } catch (error) {
      setListState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch templates'
      }));
    }
  }, [listState.filters, listState.pagination.limit, listState.pagination.offset]);

  // Create new template
  const createTemplate = useCallback(async (templateData: TemplateCreateRequest): Promise<Template> => {
    try {
      const response = await fetch('/api/neural-templates/create', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData)
      });

      if (!response.ok) {
        throw new Error(`Failed to create template (Status: ${response.status})`);
      }

      const data: TemplateResponse = await response.json();
      
      // Add to list
      setListState(prev => ({
        ...prev,
        templates: [data.template, ...prev.templates],
        pagination: {
          ...prev.pagination,
          total: prev.pagination.total + 1
        }
      }));

      return data.template;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to create template');
    }
  }, []);

  // Update template
  const updateTemplate = useCallback(async (templateData: TemplateUpdateRequest): Promise<Template> => {
    try {
      const response = await fetch(`/api/neural-templates/${templateData.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData)
      });

      if (!response.ok) {
        throw new Error(`Failed to update template (Status: ${response.status})`);
      }

      const data: TemplateResponse = await response.json();
      
      // Update in list
      setListState(prev => ({
        ...prev,
        templates: prev.templates.map(t => t.id === data.template.id ? data.template : t)
      }));

      // Update selected template if it's the same one
      if (editorState.selectedTemplate?.id === data.template.id) {
        setEditorState(prev => ({
          ...prev,
          selectedTemplate: data.template,
          isDirty: false
        }));
      }

      return data.template;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to update template');
    }
  }, [editorState.selectedTemplate?.id]);

  // Delete template
  const deleteTemplate = useCallback(async (templateId: string): Promise<void> => {
    try {
      const response = await fetch(`/api/neural-templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to delete template (Status: ${response.status})`);
      }

      // Remove from list
      setListState(prev => ({
        ...prev,
        templates: prev.templates.filter(t => t.id !== templateId),
        pagination: {
          ...prev.pagination,
          total: prev.pagination.total - 1
        }
      }));

      // Clear selection if deleted template was selected
      if (editorState.selectedTemplate?.id === templateId) {
        setEditorState(prev => ({
          ...prev,
          selectedTemplate: null,
          isEditing: false,
          isDirty: false
        }));
      }
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to delete template');
    }
  }, [editorState.selectedTemplate?.id]);

  // Generate template from voice
  const generateFromVoice = useCallback(async (voicePrompt: string, context?: any) => {
    try {
      setEditorState(prev => ({ ...prev, isGenerating: true, generationError: null }));

      const response = await fetch('/api/neural-templates/generate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voicePrompt, context })
      });

      if (!response.ok) {
        throw new Error(`Failed to generate template (Status: ${response.status})`);
      }

      const data = await response.json();
      return data.template;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate template';
      setEditorState(prev => ({ ...prev, generationError: errorMessage }));
      throw error;
    } finally {
      setEditorState(prev => ({ ...prev, isGenerating: false }));
    }
  }, []);

  // Template selection and editing
  const selectTemplate = useCallback((template: Template | null) => {
    setEditorState(prev => ({
      ...prev,
      selectedTemplate: template,
      isEditing: !!template,
      isDirty: false
    }));
  }, []);

  const startNewTemplate = useCallback(() => {
    setEditorState(prev => ({
      ...prev,
      selectedTemplate: null,
      isEditing: true,
      isDirty: false
    }));
  }, []);

  const cancelEditing = useCallback(() => {
    setEditorState(prev => ({
      ...prev,
      selectedTemplate: null,
      isEditing: false,
      isDirty: false
    }));
  }, []);

  const markDirty = useCallback(() => {
    setEditorState(prev => ({ ...prev, isDirty: true }));
  }, []);

  // Filter management
  const updateFilters = useCallback((newFilters: Partial<TemplateFilters>) => {
    const updatedFilters = { ...listState.filters, ...newFilters };
    fetchTemplates(false, updatedFilters);
  }, [listState.filters, fetchTemplates]);

  const clearFilters = useCallback(() => {
    const defaultFilters: TemplateFilters = {
      search: '',
      category: 'all',
      difficulty: 'all',
      tags: []
    };
    fetchTemplates(false, defaultFilters);
  }, [fetchTemplates]);

  // Load more templates
  const loadMore = useCallback(() => {
    if (listState.pagination.hasMore && !listState.loading) {
      fetchTemplates(true);
    }
  }, [listState.pagination.hasMore, listState.loading, fetchTemplates]);

  // Initialize templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  return {
    // State
    listState,
    editorState,

    // Template operations
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    generateFromVoice,

    // UI operations
    selectTemplate,
    startNewTemplate,
    cancelEditing,
    markDirty,

    // Filter operations
    updateFilters,
    clearFilters,
    loadMore,

    // Refresh
    refresh: () => fetchTemplates(false, listState.filters)
  };
}; 